import {
  listenForChatConversationReset,
  type ChatConversationResetReason,
} from '../../chatfe/chat/conversation-lifecycle.js';

import { formatBookingPhone, normalizeBookingPhone } from './bookingOtp.js';
import { queryAllAcrossRoots } from './domUtils.js';
import {
  OTP_CLOSED_EVENT,
  OTP_PHONE_CHANGE_EVENT,
  OTP_VERIFIED_EVENT,
  closeUnifiedBookingOtpFlow,
  openUnifiedBookingOtpFlow,
} from './unifiedBookingOtpFlow.js';
import { emitOtpFunnelClientEvent } from './otpFunnelTelemetry.js';
import { rotateOtpFunnelInstanceId } from './deviceMetadata.js';

interface ChatMetadataEventDetail {
  payload?: Record<string, unknown>;
}

type BookingOtpSource = 'booking' | 'voice' | 'text-lead';

interface BookingOtpEventDetail {
  phone?: string;
  reason?: string;
  source?: BookingOtpSource;
  verificationReceipt?: string;
}

interface BookingOtpPanelSnapshot {
  assistantMode: string | null;
  correlationId: string | null;
  cycleId: number | null;
  phone: string | null;
  pending: boolean;
  verified: boolean;
}

export interface BookingOtpUiState {
  correlationId: string | null;
  phone: string | null;
  promptVisible: boolean;
  overlayOpen: boolean;
  verified: boolean;
  cycleId: number | null;
  autoOpenedCycleId: number | null;
}

interface ChatActionSender {
  sendAction?: (
    action: {
      title: string;
      type: string;
      payload?: Record<string, unknown>;
    },
    options?: { silent?: boolean }
  ) => void;
}

interface InternalChatDrawer {
  inputEl?: HTMLTextAreaElement;
}

interface InternalChatWidget {
  _drawer?: InternalChatDrawer;
}

const PROMPT_HOST_SELECTOR = '[data-otokoc-part="booking-otp-prompt-host"]';
const OTP_UI_VERIFIED_SIGNAL_PREFIX = '__OTP_UI_VERIFIED__:';
const OTP_OVERLAY_SELECTOR = '#glov-unified-booking-otp-overlay';

function createInitialBookingOtpUiState(): BookingOtpUiState {
  return {
    correlationId: null,
    phone: null,
    promptVisible: false,
    overlayOpen: false,
    verified: false,
    cycleId: null,
    autoOpenedCycleId: null,
  };
}

export function mountBookingOtpWidgetFlow(chat: unknown): () => void {
  const internalChat = (chat || null) as (ChatActionSender & InternalChatWidget) | null;
  const chatSender = internalChat;
  const state: BookingOtpUiState = createInitialBookingOtpUiState();

  const hiddenDisplayMap = new Map<HTMLElement, string | null>();
  const shadowRootObservers = new Map<ShadowRoot, MutationObserver>();
  let observer: MutationObserver | null = null;
  let renderFrameId: number | null = null;
  let lastPromptTarget: HTMLElement | null = null;
  let activePromptHost: HTMLElement | null = null;
  let suppressOtpOverlayEvents = false;

  const scheduleRenderPrompt = () => {
    if (renderFrameId !== null) {
      return;
    }

    renderFrameId = window.requestAnimationFrame(() => {
      renderFrameId = null;
      renderPrompt();
    });
  };

  const onMetadata = (event: Event) => {
    const detail = (event as CustomEvent<ChatMetadataEventDetail>).detail;
    if (!detail?.payload || typeof detail.payload !== 'object') {
      return;
    }

    const snapshot = deriveBookingOtpPanelSnapshot(detail.payload);
    const outOfOrder =
      snapshot.cycleId !== null && state.cycleId !== null && snapshot.cycleId < state.cycleId;
    emitOtpFunnelClientEvent({
      event: 'widget_metadata_received',
      outcome: outOfOrder ? 'failed' : 'succeeded',
      reason: outOfOrder ? 'out_of_order' : 'none',
      correlationId: snapshot.correlationId,
      cycleId: snapshot.cycleId,
      dedupeKey: [
        'metadata',
        snapshot.correlationId ?? 'none',
        snapshot.cycleId ?? 'none',
        snapshot.pending,
        snapshot.verified,
        outOfOrder,
      ].join(':'),
    });
    if (outOfOrder) {
      traceBookingOtpUi('metadata:out-of-order', state, { panel: snapshot });
      return;
    }

    const previousCorrelationId = state.correlationId;
    const previousCycleId = state.cycleId;
    traceBookingOtpUi('metadata:before-sync', state, {
      panel: snapshot,
    });
    const { nextState, shouldAutoOpen } = reduceBookingOtpUiStateFromPanel(state, snapshot);
    applyBookingOtpUiState(state, nextState);
    traceBookingOtpUi('metadata:after-sync', state, {
      panel: snapshot,
    });
    if (
      previousCycleId !== null &&
      snapshot.cycleId !== null &&
      snapshot.cycleId > previousCycleId
    ) {
      emitOtpFunnelClientEvent({
        event: 'flow_terminated',
        outcome: 'superseded',
        reason: 'new_booking_cycle',
        correlationId: previousCorrelationId,
        cycleId: previousCycleId,
        dedupeKey: `superseded:${previousCorrelationId ?? 'none'}:${previousCycleId}`,
      });
    }

    if (
      shouldAutoOpen &&
      shouldAutoOpenBookingOtpOverlayForViewport(
        typeof window === 'undefined' ? null : window.innerWidth
      )
    ) {
      traceBookingOtpUi('metadata:auto-open-overlay', state);
      emitOtpFunnelClientEvent({
        event: 'desktop_auto_open',
        outcome: 'attempted',
        reason: 'none',
        correlationId: state.correlationId,
        cycleId: state.cycleId,
        dedupeKey: `desktop-auto-open-attempt:${state.correlationId ?? 'none'}`,
      });
      openOtpOverlay();
      emitOtpFunnelClientEvent({
        event: 'desktop_auto_open',
        outcome: document.querySelector(OTP_OVERLAY_SELECTOR) ? 'succeeded' : 'failed',
        reason: document.querySelector(OTP_OVERLAY_SELECTOR) ? 'none' : 'target_absent',
        correlationId: state.correlationId,
        cycleId: state.cycleId,
        dedupeKey: `desktop-auto-open-result:${state.correlationId ?? 'none'}`,
      });
      return;
    }

    scheduleRenderPrompt();
  };

  const onChatOpen = () => {
    window.setTimeout(() => {
      scheduleRenderPrompt();
    }, 30);
  };

  const onPhoneChange = (event: Event) => {
    if (suppressOtpOverlayEvents) {
      return;
    }
    const detail = (event as CustomEvent<BookingOtpEventDetail>).detail;
    if (!shouldHandleBookingOtpEvent(detail, state.cycleId)) {
      traceBookingOtpUi('phone-change:ignored', state, { detail });
      return;
    }
    if (typeof detail?.phone === 'string' && detail.phone.trim()) {
      applyBookingOtpUiState(state, reduceBookingOtpUiStateWithPhoneChange(state, detail.phone));
      traceBookingOtpUi('phone-change', state, { detail });
      scheduleRenderPrompt();
    }
  };

  const onClosed = (event: Event) => {
    if (suppressOtpOverlayEvents) {
      return;
    }
    const detail = (event as CustomEvent<BookingOtpEventDetail>).detail;
    if (!shouldHandleBookingOtpEvent(detail, state.cycleId)) {
      traceBookingOtpUi('closed:ignored', state, { detail });
      return;
    }
    applyBookingOtpUiState(state, reduceBookingOtpUiStateWithClose(state, detail?.reason));
    traceBookingOtpUi('closed', state, { detail });
    scheduleRenderPrompt();
  };

  const resetForConversationReplacement = (reason: ChatConversationResetReason) => {
    const previousState = { ...state };
    // assistant-fe aborts active requests on new chat but intentionally keeps
    // its configured session ID. Rotate the non-PII funnel instance before the
    // next request so a legitimate restarted cycle 1 cannot collide with the
    // prior conversation's correlation or dedupe keys.
    rotateOtpFunnelInstanceId();
    applyBookingOtpUiState(state, resetBookingOtpUiStateForNewChat());
    traceBookingOtpUi('new-chat:reset', state);
    for (const host of queryAllAcrossRoots(PROMPT_HOST_SELECTOR)) {
      host.remove();
    }
    activePromptHost = null;
    lastPromptTarget = null;
    restoreInputElements(hiddenDisplayMap);
    emitOtpFunnelClientEvent({
      event: 'flow_terminated',
      outcome: 'reset',
      reason,
      correlationId: previousState.correlationId,
      cycleId: previousState.cycleId,
    });
    scheduleRenderPrompt();
    suppressOtpOverlayEvents = true;
    try {
      closeUnifiedBookingOtpFlow();
    } finally {
      suppressOtpOverlayEvents = false;
    }
  };

  const removeConversationResetListener = listenForChatConversationReset(({ reason }) => {
    resetForConversationReplacement(reason);
  });

  const onVerified = (event: Event) => {
    if (suppressOtpOverlayEvents) {
      return;
    }
    const detail = (event as CustomEvent<BookingOtpEventDetail>).detail;
    if (!shouldHandleBookingOtpEvent(detail, state.cycleId)) {
      traceBookingOtpUi('verified:ignored', state, { detail });
      return;
    }
    applyBookingOtpUiState(state, reduceBookingOtpUiStateWithVerification(state, detail.phone));
    traceBookingOtpUi('verified', state, { detail });
    scheduleRenderPrompt();
  };

  const openOtpOverlay = () => {
    applyBookingOtpUiState(state, reduceBookingOtpUiStateWithOverlayOpen(state));
    traceBookingOtpUi('open-overlay', state);
    scheduleRenderPrompt();
    openUnifiedBookingOtpFlow({
      initialPhone: state.phone,
      source: 'booking',
      bookingCycleId: state.cycleId,
      correlationId: state.correlationId,
      onVerified: (phone, verificationReceipt) => {
        applyBookingOtpUiState(state, reduceBookingOtpUiStateWithVerification(state, phone));
        traceBookingOtpUi('overlay-verified-callback', state, { phone });
        sendOtpVerifiedSignal(chatSender, phone, verificationReceipt);
        scheduleRenderPrompt();
      },
      onClose: (reason) => {
        applyBookingOtpUiState(state, reduceBookingOtpUiStateWithClose(state, reason));
        traceBookingOtpUi('overlay-close-callback', state, { reason });
        scheduleRenderPrompt();
      },
    });
  };

  function ensureHost(): HTMLElement | null {
    const mobilePromptTelemetry = !shouldAutoOpenBookingOtpOverlayForViewport(
      typeof window === 'undefined' ? null : window.innerWidth
    );
    const target = resolveBookingOtpPromptTarget(internalChat);
    if (!target) {
      if (mobilePromptTelemetry && state.promptVisible) {
        emitOtpFunnelClientEvent({
          event: 'mobile_prompt_target',
          outcome: 'missing',
          reason: 'target_absent',
          correlationId: state.correlationId,
          cycleId: state.cycleId,
          dedupeKey: `prompt-target-missing:${state.correlationId ?? 'none'}`,
        });
      }
      return null;
    }
    const targetReplaced = Boolean(lastPromptTarget && lastPromptTarget !== target);
    lastPromptTarget = target;
    if (mobilePromptTelemetry) {
      emitOtpFunnelClientEvent({
        event: 'mobile_prompt_target',
        outcome: 'found',
        reason: targetReplaced ? 'target_replaced' : 'none',
        correlationId: state.correlationId,
        cycleId: state.cycleId,
        dedupeKey: [
          'prompt-target',
          state.correlationId ?? 'none',
          targetReplaced ? 'replaced' : 'found',
        ].join(':'),
      });
    }

    const parent = target.parentElement;
    if (!parent) {
      return null;
    }

    let host = parent.querySelector(PROMPT_HOST_SELECTOR) as HTMLElement | null;
    const existingHost = Boolean(host);
    if (!host) {
      host = document.createElement('div');
      host.dataset.otokocPart = 'booking-otp-prompt-host';
      parent.insertBefore(host, target.nextSibling);
    } else if (host.previousElementSibling !== target) {
      parent.insertBefore(host, target.nextSibling);
    }

    if (mobilePromptTelemetry) {
      emitOtpFunnelClientEvent({
        event: 'mobile_prompt',
        outcome: targetReplaced || existingHost ? 'reattached' : 'mounted',
        reason: targetReplaced ? 'target_replaced' : 'none',
        correlationId: state.correlationId,
        cycleId: state.cycleId,
        dedupeKey: [
          'prompt',
          state.correlationId ?? 'none',
          targetReplaced ? 'reattached' : 'mounted',
        ].join(':'),
      });
    }
    activePromptHost = host;
    return host;
  }

  function renderPrompt(): void {
    const shouldShowPrompt = state.promptVisible && !state.overlayOpen && !state.verified;
    if (!state.promptVisible && !state.overlayOpen) {
      restoreInputElements(hiddenDisplayMap);
      if (activePromptHost?.isConnected) {
        activePromptHost.replaceChildren();
        activePromptHost.style.display = 'none';
      }
      return;
    }
    const host = ensureHost();
    const shouldHideInput = state.overlayOpen || (shouldShowPrompt && Boolean(host));

    if (shouldHideInput) {
      hideInputElements(hiddenDisplayMap);
    } else {
      restoreInputElements(hiddenDisplayMap);
    }
    if (!host) {
      return;
    }

    if (!shouldShowPrompt) {
      host.replaceChildren();
      host.style.display = 'none';
      return;
    }

    host.style.display = 'block';
    host.style.borderTop = '1px solid #edf1f7';
    host.style.background = '#ffffff';
    host.style.padding = '14px 16px';
    host.style.boxShadow = '0 -12px 28px rgba(23, 32, 51, 0.08)';

    const card = createPromptCard(state.phone, openOtpOverlay);
    host.replaceChildren(card);
  }

  const refreshKnownWidgetShadowObservers = () => {
    for (const [root, rootObserver] of shadowRootObservers) {
      if (!root.host.isConnected) {
        rootObserver.disconnect();
        shadowRootObservers.delete(root);
      }
    }

    for (const host of document.querySelectorAll<HTMLElement>('[data-gengage-widget]')) {
      const root = host.shadowRoot;
      if (!root || shadowRootObservers.has(root)) {
        continue;
      }
      const rootObserver = new MutationObserver((records) => {
        if (!state.promptVisible && !state.overlayOpen) {
          return;
        }
        const onlyPromptMutations = records.every((record) => {
          const target = record.target;
          return (
            target instanceof Element &&
            (target.matches(PROMPT_HOST_SELECTOR) || Boolean(target.closest(PROMPT_HOST_SELECTOR)))
          );
        });
        if (onlyPromptMutations) {
          return;
        }
        scheduleRenderPrompt();
      });
      rootObserver.observe(root, { childList: true, subtree: true });
      shadowRootObservers.set(root, rootObserver);
    }
  };

  observer = new MutationObserver(() => {
    refreshKnownWidgetShadowObservers();
    if (state.promptVisible || state.overlayOpen) {
      scheduleRenderPrompt();
    }
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  window.addEventListener('gengage:chat:metadata', onMetadata as EventListener);
  window.addEventListener('gengage:chat:open', onChatOpen);
  document.addEventListener(OTP_PHONE_CHANGE_EVENT, onPhoneChange as EventListener);
  document.addEventListener(OTP_CLOSED_EVENT, onClosed);
  document.addEventListener(OTP_VERIFIED_EVENT, onVerified as EventListener);
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'hidden' || !state.promptVisible || state.verified) {
      return;
    }
    emitOtpFunnelClientEvent({
      event: 'flow_terminated',
      outcome: 'abandoned',
      reason: 'page_hidden',
      correlationId: state.correlationId,
      cycleId: state.cycleId,
      dedupeKey: `page-hidden:${state.correlationId ?? 'none'}`,
    });
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  refreshKnownWidgetShadowObservers();
  scheduleRenderPrompt();

  return () => {
    observer?.disconnect();
    observer = null;
    for (const rootObserver of shadowRootObservers.values()) {
      rootObserver.disconnect();
    }
    shadowRootObservers.clear();
    if (renderFrameId !== null) {
      window.cancelAnimationFrame(renderFrameId);
      renderFrameId = null;
    }
    window.removeEventListener('gengage:chat:metadata', onMetadata as EventListener);
    window.removeEventListener('gengage:chat:open', onChatOpen);
    removeConversationResetListener();
    document.removeEventListener(OTP_PHONE_CHANGE_EVENT, onPhoneChange as EventListener);
    document.removeEventListener(OTP_CLOSED_EVENT, onClosed);
    document.removeEventListener(OTP_VERIFIED_EVENT, onVerified as EventListener);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    if (state.promptVisible && !state.verified) {
      emitOtpFunnelClientEvent({
        event: 'flow_terminated',
        outcome: 'abandoned',
        reason: 'widget_destroyed',
        correlationId: state.correlationId,
        cycleId: state.cycleId,
        dedupeKey: `widget-destroyed:${state.correlationId ?? 'none'}`,
      });
    }

    for (const host of queryAllAcrossRoots(PROMPT_HOST_SELECTOR)) {
      host.remove();
    }

    closeUnifiedBookingOtpFlow();
    restoreInputElements(hiddenDisplayMap);
  };
}

function sendOtpVerifiedSignal(
  chatSender: ChatActionSender | null,
  phone: string,
  verificationReceipt?: string
): void {
  if (!chatSender?.sendAction || !verificationReceipt?.trim()) {
    return;
  }

  chatSender.sendAction(
    {
      title: '',
      type: 'inputText',
      payload: {
        text: `${OTP_UI_VERIFIED_SIGNAL_PREFIX}${JSON.stringify({
          phone,
          verificationReceipt,
        })}`,
      },
    },
    { silent: true }
  );
}

function createPromptCard(phone: string | null, onOpenOtp: () => void): HTMLElement {
  const card = document.createElement('div');
  card.style.boxSizing = 'border-box';
  card.style.width = '100%';
  card.style.minWidth = '0';
  card.style.maxWidth = '560px';
  card.style.margin = '0 auto';
  card.style.display = 'flex';
  card.style.flexDirection = 'column';
  card.style.gap = '12px';
  card.style.borderRadius = '24px';
  card.style.border = '1px solid #edf1f7';
  card.style.background = '#fffaf6';
  card.style.padding = '16px';

  const topRow = document.createElement('div');
  topRow.style.display = 'flex';
  topRow.style.alignItems = 'flex-start';
  topRow.style.gap = '12px';

  const iconWrap = document.createElement('div');
  iconWrap.style.width = '44px';
  iconWrap.style.height = '44px';
  iconWrap.style.minWidth = '44px';
  iconWrap.style.borderRadius = '16px';
  iconWrap.style.display = 'flex';
  iconWrap.style.alignItems = 'center';
  iconWrap.style.justifyContent = 'center';
  iconWrap.style.background = 'color-mix(in srgb, var(--client-primary, #f97d1a) 10%, white)';
  iconWrap.style.color = 'var(--client-primary, #f97d1a)';
  iconWrap.innerHTML =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2l7 3v6c0 5-3.4 9.7-7 11-3.6-1.3-7-6-7-11V5l7-3z"/><path d="M9.5 11.5l2 2 3-3"/></svg>';

  const copyWrap = document.createElement('div');
  copyWrap.style.minWidth = '0';
  copyWrap.style.flex = '1';

  const eyebrow = document.createElement('p');
  eyebrow.textContent = 'Son Adım';
  eyebrow.style.margin = '0';
  eyebrow.style.color = 'var(--client-primary, #f97d1a)';
  eyebrow.style.fontSize = '12px';
  eyebrow.style.fontWeight = '700';
  eyebrow.style.letterSpacing = '0.16em';
  eyebrow.style.textTransform = 'uppercase';

  const title = document.createElement('h3');
  title.textContent = 'Talebinizi SMS ile doğrulayın';
  title.style.margin = '4px 0 0';
  title.style.color = '#172033';
  title.style.fontSize = '19px';
  title.style.lineHeight = '1.25';
  title.style.fontWeight = '700';

  const subtitle = document.createElement('p');
  subtitle.textContent = phone
    ? `${formatBookingPhone(phone)} numarasıyla devam edelim.`
    : 'Telefon numaranızı doğrulayarak talebinizi tamamlayın.';
  subtitle.style.margin = '8px 0 0';
  subtitle.style.color = '#5f6d84';
  subtitle.style.fontSize = '15px';
  subtitle.style.lineHeight = '1.45';

  copyWrap.appendChild(eyebrow);
  copyWrap.appendChild(title);
  copyWrap.appendChild(subtitle);

  topRow.appendChild(iconWrap);
  topRow.appendChild(copyWrap);

  const actionButton = document.createElement('button');
  actionButton.type = 'button';
  actionButton.textContent = 'SMS Doğrulamasına Geç';
  actionButton.style.boxSizing = 'border-box';
  actionButton.style.width = '100%';
  actionButton.style.border = '0';
  actionButton.style.borderRadius = '18px';
  actionButton.style.background = 'var(--client-primary, #f97d1a)';
  actionButton.style.color = '#ffffff';
  actionButton.style.padding = '14px 18px';
  actionButton.style.fontSize = '15px';
  actionButton.style.fontWeight = '700';
  actionButton.style.cursor = 'pointer';
  actionButton.addEventListener('click', onOpenOtp);

  card.appendChild(topRow);
  card.appendChild(actionButton);

  return card;
}

export function shouldHandleBookingOtpEvent(
  detail: BookingOtpEventDetail | undefined,
  activeTextBookingCycleId: number | null = null
): boolean {
  if (!detail?.source || detail.source === 'booking') {
    return true;
  }

  return detail.source === 'voice' && activeTextBookingCycleId === null;
}

/**
 * Build a pristine state after the SDK reports a conversation replacement.
 */
export function resetBookingOtpUiStateForNewChat(): BookingOtpUiState {
  return createInitialBookingOtpUiState();
}

function normalizePhoneDigits(phone: string | null): string {
  const normalized = normalizeBookingPhone(phone || '');
  return normalized ?? (phone || '').replace(/\D+/g, '');
}

export function syncBookingOtpUiStateFromPanel(
  payload: Record<string, unknown>,
  state: BookingOtpUiState
): void {
  applyBookingOtpUiState(
    state,
    reduceBookingOtpUiStateFromPanel(state, deriveBookingOtpPanelSnapshot(payload)).nextState
  );
}

function resolveBookingOtpPromptTarget(
  internalChat: InternalChatWidget | null
): HTMLElement | null {
  const inputArea = internalChat?._drawer?.inputEl?.closest(
    '[data-gengage-part="chat-input-area"]'
  );
  if (inputArea instanceof HTMLElement && inputArea.isConnected) {
    return inputArea;
  }

  return (
    queryAllAcrossRoots('[data-gengage-part="chat-footer"]').find((target) => target.isConnected) ??
    null
  );
}

function applyBookingOtpUiState(state: BookingOtpUiState, nextState: BookingOtpUiState): void {
  state.correlationId = nextState.correlationId;
  state.phone = nextState.phone;
  state.promptVisible = nextState.promptVisible;
  state.overlayOpen = nextState.overlayOpen;
  state.verified = nextState.verified;
  state.cycleId = nextState.cycleId;
  state.autoOpenedCycleId = nextState.autoOpenedCycleId;
}

function deriveBookingOtpPanelSnapshot(payload: Record<string, unknown>): BookingOtpPanelSnapshot {
  const panel = asRecord(payload.panel);
  const threadState = asRecord(panel?.agent_thread_state);
  if (!panel) {
    return {
      assistantMode: null,
      correlationId: null,
      cycleId: null,
      phone: null,
      pending: false,
      verified: false,
    };
  }

  const threadMode = readString(threadState?.mode);
  return {
    assistantMode:
      threadMode === 'booking_active' || threadMode === 'otp_pending'
        ? 'booking'
        : readString(panel.assistant_mode),
    correlationId: readCorrelationId(panel.otp_funnel_correlation_id),
    cycleId: readBookingCycleId(panel),
    phone: readOtpPhone(panel),
    pending: isOtpPending(panel),
    verified: isOtpVerified(panel),
  };
}

function reduceBookingOtpUiStateFromPanel(
  currentState: BookingOtpUiState,
  snapshot: BookingOtpPanelSnapshot
): { nextState: BookingOtpUiState; shouldAutoOpen: boolean } {
  if (snapshot.assistantMode !== 'booking') {
    if (
      currentState.cycleId === null &&
      currentState.promptVisible &&
      !currentState.overlayOpen &&
      !currentState.verified &&
      Boolean(currentState.phone)
    ) {
      // Voice OTP handoff recovery can legitimately surface the shared footer
      // CTA while the text widget stays in shopping mode. Preserve that CTA
      // until the user acts on it or the conversation is reset.
      return {
        nextState: { ...currentState },
        shouldAutoOpen: false,
      };
    }

    return {
      nextState: createInitialBookingOtpUiState(),
      shouldAutoOpen: false,
    };
  }
  if (
    snapshot.cycleId !== null &&
    currentState.cycleId !== null &&
    snapshot.cycleId < currentState.cycleId
  ) {
    return {
      nextState: { ...currentState },
      shouldAutoOpen: false,
    };
  }

  const nextState: BookingOtpUiState = { ...currentState };
  const cycleChanged = snapshot.cycleId !== null && snapshot.cycleId !== currentState.cycleId;

  if (cycleChanged) {
    nextState.cycleId = snapshot.cycleId;
    nextState.correlationId = snapshot.correlationId;
    nextState.verified = false;
    nextState.promptVisible = false;
    nextState.overlayOpen = false;
  }
  if (snapshot.correlationId) {
    nextState.correlationId = snapshot.correlationId;
  }

  if (snapshot.phone && snapshot.phone !== nextState.phone) {
    // Formatting-only differences (e.g. "0532 555 12 34" vs "05325551234")
    // must not invalidate an existing verification.
    const sameNumber =
      normalizePhoneDigits(snapshot.phone) === normalizePhoneDigits(nextState.phone);
    nextState.phone = snapshot.phone;
    if (!sameNumber) {
      nextState.verified = false;
    }
  }

  if (snapshot.verified) {
    nextState.verified = true;
    nextState.promptVisible = false;
    nextState.overlayOpen = false;
    return {
      nextState,
      shouldAutoOpen: false,
    };
  }

  if (snapshot.pending) {
    if (!nextState.verified) {
      nextState.promptVisible = true;
    }

    const shouldAutoOpen =
      nextState.promptVisible &&
      !nextState.overlayOpen &&
      !nextState.verified &&
      nextState.cycleId !== null &&
      nextState.autoOpenedCycleId !== nextState.cycleId &&
      (cycleChanged || !currentState.promptVisible);

    return {
      nextState,
      shouldAutoOpen,
    };
  }

  // Booking mode but no longer otp_pending: the cycle completed or moved on.
  // Keep `verified` so a later overlay-close event cannot resurrect the prompt
  // for an already-verified cycle; cycle changes above reset it explicitly.
  nextState.promptVisible = false;
  nextState.overlayOpen = false;
  return {
    nextState,
    shouldAutoOpen: false,
  };
}

function reduceBookingOtpUiStateWithPhoneChange(
  currentState: BookingOtpUiState,
  phone: string
): BookingOtpUiState {
  return {
    ...currentState,
    phone: phone.trim(),
    verified: false,
  };
}

export function reduceBookingOtpUiStateWithClose(
  currentState: BookingOtpUiState,
  reason?: string
): BookingOtpUiState {
  if (reason === 'verified') {
    return {
      ...currentState,
      verified: true,
      promptVisible: false,
      overlayOpen: false,
    };
  }

  return {
    ...currentState,
    // Keep the footer CTA reachable after dismissing the modal unless OTP is already verified.
    promptVisible: currentState.verified ? false : Boolean(currentState.phone),
    overlayOpen: false,
  };
}

function reduceBookingOtpUiStateWithVerification(
  currentState: BookingOtpUiState,
  phone?: string
): BookingOtpUiState {
  return {
    ...currentState,
    phone: typeof phone === 'string' && phone.trim() ? phone.trim() : currentState.phone,
    verified: true,
    promptVisible: false,
    overlayOpen: false,
  };
}

function reduceBookingOtpUiStateWithOverlayOpen(
  currentState: BookingOtpUiState
): BookingOtpUiState {
  return {
    ...currentState,
    overlayOpen: true,
    autoOpenedCycleId: currentState.cycleId,
  };
}

function readBookingCycleId(panel: Record<string, unknown>): number | null {
  const threadState = asRecord(panel.agent_thread_state);
  const booking = asRecord(threadState?.booking);
  const cycleId = booking?.cycleId;
  if (typeof cycleId !== 'number' || !Number.isFinite(cycleId)) {
    return null;
  }
  return Math.trunc(cycleId);
}

function readOtpPhone(panel: Record<string, unknown>): string | null {
  const threadState = asRecord(panel.agent_thread_state);
  const booking = asRecord(threadState?.booking);
  const fields = asRecord(booking?.fields);

  return readString(fields?.phone) || null;
}

function isOtpPending(panel: Record<string, unknown>): boolean {
  const threadState = asRecord(panel.agent_thread_state);
  const mode = readString(threadState?.mode);
  return mode === 'otp_pending';
}

function isOtpVerified(panel: Record<string, unknown>): boolean {
  const threadState = asRecord(panel.agent_thread_state);
  const booking = asRecord(threadState?.booking);
  return booking?.otpVerified === true;
}

function traceBookingOtpUi(
  event: string,
  state: BookingOtpUiState,
  extra: Record<string, unknown> = {}
): void {
  void event;
  void state;
  void extra;
}

export function toSafeBookingOtpDebugExtra(
  extra: Record<string, unknown>
): Record<string, unknown> {
  const panel = asRecord(extra.panel);
  const detail = asRecord(extra.detail);
  return {
    ...(panel
      ? {
          panel: {
            assistantMode: readString(panel.assistantMode),
            cycleId:
              typeof panel.cycleId === 'number' && Number.isFinite(panel.cycleId)
                ? Math.trunc(panel.cycleId)
                : null,
            hasPhone: Boolean(readString(panel.phone)),
            pending: panel.pending === true,
            verified: panel.verified === true,
          },
        }
      : {}),
    ...(detail
      ? {
          detail: {
            source: readString(detail.source),
            reason: readString(detail.reason),
            hasPhone: Boolean(readString(detail.phone)),
            hasVerificationReceipt: Boolean(readString(detail.verificationReceipt)),
          },
        }
      : {}),
    ...(typeof extra.reason === 'string' ? { reason: extra.reason.slice(0, 32) } : {}),
  };
}

function hideInputElements(hiddenDisplayMap: Map<HTMLElement, string | null>): void {
  blurFocusedBookingInput();

  for (const selector of [
    '[data-gengage-part="chat-input-area"]',
    '[data-gengage-part="chat-input-chips"]',
  ]) {
    for (const element of queryAllAcrossRoots(selector)) {
      if (!hiddenDisplayMap.has(element)) {
        hiddenDisplayMap.set(element, element.style.display || null);
      }
      element.style.display = 'none';
    }
  }
}

function restoreInputElements(hiddenDisplayMap: Map<HTMLElement, string | null>): void {
  for (const [element, previousDisplay] of hiddenDisplayMap.entries()) {
    if (!element.isConnected) {
      continue;
    }

    if (previousDisplay) {
      element.style.display = previousDisplay;
    } else {
      element.style.removeProperty('display');
    }
  }

  hiddenDisplayMap.clear();
}

function blurFocusedBookingInput(): void {
  for (const inputArea of queryAllAcrossRoots('[data-gengage-part="chat-input-area"]')) {
    const root = inputArea.getRootNode();
    const activeElement =
      root instanceof Document || root instanceof ShadowRoot ? root.activeElement : null;

    if (activeElement instanceof HTMLElement && inputArea.contains(activeElement)) {
      activeElement.blur();
      return;
    }

    const focusedInput = inputArea.querySelector<HTMLElement>(
      'input:focus, textarea:focus, [contenteditable="true"]:focus'
    );
    if (focusedInput) {
      focusedInput.blur();
      return;
    }
  }

  const activeElement = document.activeElement;
  const inputArea = activeElement?.closest?.('[data-gengage-part="chat-input-area"]');
  if (
    activeElement instanceof HTMLElement &&
    inputArea instanceof HTMLElement &&
    typeof activeElement.blur === 'function'
  ) {
    activeElement.blur();
  }
}

export function shouldAutoOpenBookingOtpOverlayForViewport(
  viewportWidth: number | null | undefined
): boolean {
  return typeof viewportWidth !== 'number' || viewportWidth > 768;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function readCorrelationId(value: unknown): string | null {
  const normalized = readString(value);
  return normalized && /^[a-f0-9]{32}$/u.test(normalized) ? normalized : null;
}
